import {PromotionTracker} from '../../../../../../../packages/core/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {
  BasketAction,
  MainFilterSelectionEvent,
  RecoSectionContent,
  TrackingBasket,
  TrackingInitialPageContext,
  TrackingPageContext,
  TrackingPageTypeWithId,
  TrackingPageTypeWithoutId,
  TrackingWishlistItemId,
} from '../../../types';
import {InternalFilterSelectionEvent} from '../trackingHandlers/trackFilterSelection';
import {exists} from './exists';
import {readStorageValue, writeStorageValue} from './localStorage';
import {stringifyEventData} from './stringifyEventData';

export interface DataLayerProductGA3 {
  id: number;
  name: string;
  brand: string;
  quantity: number;
  variant: number | null;

  category: string | null;
  categoryId: number | null;
  sourceCategory: string | null;
  sourceCategoryId: number | null;

  price: string;
  strikeOutPrice: string | null;
  tax: string;

  position: number;
  sectionIndexOnPage: number;
  rowIndexInSection: number;
  columnIndexInSection: number;

  initiallyVisibleColumns?: number;

  isInSale: boolean;
  saleAmount: string;
  isSoldOut: boolean;

  source: string | null;
  sourceHistoryOne: string | null;
  sourceHistoryTwo: string | null;
  sourceHistoryThree: string | null;

  destinationUrl: string;

  sponsored: string;

  sort: string;
  filter: string;
  fitAnalyticsUsed: string;
  supplierId?: number;
  sizeLabel?: string;
  masterKey: string;
  merchantId?: number | string | null;
  price_bi: string;
  creative_name: PromotionTracker['creativeName'];
  promotion_id: PromotionTracker['promotionId'];
}

export interface DataLayerProductGA4 {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_brand_id: string;
  item_badge: string;
  quantity: number;
  item_variant: string;
  currency: string;

  item_category: string | null;
  item_category_id: number | null;

  price: number;
  original_price: number | null;
  tax: number;
  index: number | null;

  sale_discount: number;
  sold_out: boolean;

  sponsored: string;

  sort: string;
  filter: string;
  fit_analytics_used: string;
  supplier_id?: string;
  size_label?: string | undefined;
  master_key: string;
  merchant_id?: number | string | null;
  price_bi: string;
  campaign_discount: number;
  item_list_name: string;
  item_list_id: string;
  source: string | null;
  position_bi: string;
  item_list_index: number | null;
  location_id: string;
  destination: string;
  destination_url: string;
  linktarget_id: string;
  linktarget: string | null;
  creative_name: PromotionTracker['creativeName'];
  promotion_id: PromotionTracker['promotionId'];
  image_set?: string;
  image_type?: string;
}

export interface EventPageData {
  content_name: string;
  page_type: string;
  page_type_id: string;
}

export interface DetailedPageInfo {
  type: 'detailed';
  page: TrackingPageContext;
  /** When the page initially started loading */
  timestamp: number;
  /**
   * These are filled when leaving the current page with a click,
   * so we have more information on the next page
   */
  sectionType?: string;
  sectionTypeId?: string | RecoSectionContent;
  linkedContentType?: string;
  linkedContentTypeId?: number;
  /** Only gets set when we receive a product click from an active
   * sort channel's product stream
   */
  sortChannel?: string;
}
export interface InitialPageInfo {
  type: 'initial';
  page: TrackingInitialPageContext;
  /** When the page initially started loading */
  timestamp: number;
}

export interface FeatureFlags {
  shouldTrackOriginClick: boolean;
  enableGAVersion: {
    GA4Version: boolean;
  };
}

export interface TrackingClientState {
  shop: {
    shopId: number;
    childShopId: number | null;
    appVersion: string;
    gitHash: string;
  };
  pageHistory: DetailedPageInfo[];
  lastVisitedCategory: {id: number | null; url: string | null};
  previousProductId?: number;
  currentPage?: InitialPageInfo | DetailedPageInfo;
  basket: TrackingBasket;
  basketItems: {id: number; quantity: number; sizeId: number}[];
  queuedBasketActions: BasketAction[];
  wishlist: {items: TrackingWishlistItemId[]};
  customerDataInitialized: boolean;
  fitAnalyticsUsed: string;
  /** All currently actively selected filters by filter slug */
  selectedFilters: Partial<Record<string, InternalFilterSelectionEvent>>;
  sort: string;
  currency: string;
  landingPage: string;
  landingPageTracked: boolean;
  productImpressionQueue: {
    GA4: DataLayerProductGA4[];
  };
  productImpressionQueueTimeout?: number;
  /** Sections indices of the sections that have already been tracked on a given page */
  firedRecoLists: Partial<Record<string, number[]>>;
  currentTrackingPageContext?: TrackingPageContext;
  originClick: string;
  featureFlags: FeatureFlags;
  sortChannel?: string;

  shopGender?: string;
  parameter?: string;
  referrer?: string;
  locale?: string;
}

const PAGE_HISTORY_PERSIST_KEY = `aywt_pageHistory`;
const LAST_CATEGORY_PERSIST_KEY = `aywt_lastCategory`;
const LAST_PRODUCT_ID_PERSIST_KEY = `aywt_lastProductID`;

export interface initialClientStateTypes {
  shopId: number;
  childShopId: number | null;
  appVersion: string;
  gitHash: string;
  featureFlags: FeatureFlags;
  locale?: string;
  shop_currency?: string;
  parameter?: string;
  referrer?: string;
  shop_gender?: string;
  externalLogger?: ExternalReporterCallSignature;
  noop?: boolean;
  currentPage?: InitialPageInfo;
}

const createInitialState = ({
  shopId,
  childShopId,
  appVersion,
  gitHash,
  featureFlags,
  noop = true,
  locale,
  shop_currency,
  parameter,
  referrer,
  shop_gender,
  currentPage,
}: initialClientStateTypes): TrackingClientState => {
  const pageHistory = readStorageValue<TrackingClientState['pageHistory']>(
    PAGE_HISTORY_PERSIST_KEY,
    []
  );

  const lastVisitedCategory = readStorageValue<
    TrackingClientState['lastVisitedCategory']
  >(LAST_CATEGORY_PERSIST_KEY, {id: null, url: null});

  const previousProductId = readStorageValue<
    TrackingClientState['previousProductId']
  >(LAST_PRODUCT_ID_PERSIST_KEY, undefined);

  const landingPage = noop
    ? document.location.pathname + document.location.search
    : 'server side';

  return {
    shop: {
      shopId,
      childShopId,
      appVersion,
      gitHash,
    },
    pageHistory,
    previousProductId,
    lastVisitedCategory,
    currentPage,
    basketItems: [],
    basket: {
      items: [],
      total: {
        campaignReductionWithTax: 0,
        currency: '',
        saleReductionWithTax: 0,
        withTax: 0,
        withoutTax: 0,
        fullPrice: 0,
        couponReductionWithTax: 0,
        coinsReductionWithTax: 0,
      },
    },
    wishlist: {items: []},
    queuedBasketActions: [],
    customerDataInitialized: false,
    fitAnalyticsUsed: 'user_data_unavailable|size_not_preselected',
    selectedFilters: {},
    sort: '',
    currency: shop_currency || '',
    landingPage,
    landingPageTracked: false,
    productImpressionQueue: {
      GA4: [],
    },
    firedRecoLists: {},
    sortChannel: undefined,
    originClick: '',
    featureFlags,
    shopGender: shop_gender,
    parameter,
    referrer,
    locale,
  };
};

type logLevel = 'info' | 'warn' | 'error';

interface ReportOptions {
  [key: string]: string;
}

export interface ExternalReporterCallSignature {
  (level: logLevel, message: Error | string, options?: ReportOptions): void;
}

export interface TrackingClientStateHolder {
  update: (changes: Partial<TrackingClientState>) => TrackingClientState;
  getCurrent: () => TrackingClientState;
  report?: (
    level: logLevel,
    message: Error | string,
    options?: ReportOptions
  ) => void;
}

export const createStateProvider = ({
  shopId,
  childShopId,
  appVersion,
  gitHash,
  featureFlags,
  externalLogger,
  locale,
  shop_currency,
  parameter,
  referrer,
  shop_gender,
  currentPage,
}: initialClientStateTypes): TrackingClientStateHolder => {
  let currentState = createInitialState({
    shopId,
    childShopId,
    appVersion,
    gitHash,
    featureFlags,
    locale,
    shop_currency,
    parameter,
    referrer,
    shop_gender,
    currentPage,
  });

  const update = (
    changes: Partial<TrackingClientState>
  ): TrackingClientState => {
    if (changes.pageHistory) {
      writeStorageValue(PAGE_HISTORY_PERSIST_KEY, changes.pageHistory);
    }
    if (changes.lastVisitedCategory) {
      writeStorageValue(LAST_CATEGORY_PERSIST_KEY, changes.lastVisitedCategory);
    }
    if (changes.previousProductId) {
      writeStorageValue(LAST_PRODUCT_ID_PERSIST_KEY, changes.previousProductId);
    }

    currentState = {...currentState, ...changes};

    return currentState;
  };
  const getCurrent = (): TrackingClientState => {
    return currentState;
  };

  const report = () => {
    if (externalLogger !== undefined) {
      return {
        report: (
          level: logLevel,
          message: Error | string,
          options?: ReportOptions
        ) => externalLogger(level, message, options),
      };
    }
  };

  return {getCurrent, update, report};
};

function isFilterActive(filter: MainFilterSelectionEvent) {
  switch (filter.filterType) {
    case 'attribute': {
      return filter.selectedValue.length > 0;
    }
    case 'range': {
      return true;
    }
    case 'boolean': {
      return filter.selectedValue;
    }
  }
}

export const getFilterString = (state: TrackingClientState) => {
  if (
    state.currentPage?.page.type &&
    filterablePages.has(state.currentPage?.page.type)
  ) {
    return '';
  }

  const filters = Object.values(state.selectedFilters)
    .filter(exists)
    .filter(filter => {
      return filter && (filter.type === 'quick' || isFilterActive(filter));
    });

  const eventData = filters.map(filter => {
    let values: Array<string | number | boolean | null | undefined> = [];
    if (filter && filter.type === 'quick') {
      values = [filter.selectedValue];
    } else if (filter) {
      values =
        'filterType' in filter && filter.filterType === 'boolean'
          ? [filter.selectedValue]
          : (filter.selectedValue as number[] | boolean[]);
    }
    const stringyfiedValues = values.filter(exists).join(',');

    return `${filter?.slug}:${stringyfiedValues}`;
  });

  return stringifyEventData(eventData);
};

const filterablePages = new Set([
  TrackingPageTypeWithId.Category,
  TrackingPageTypeWithId.Brand,
  TrackingPageTypeWithId.Story,
  TrackingPageTypeWithoutId.Search,
]);
